import React from 'react';
import styles from './CompanyFilesLayout.module.scss';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { closeFileExplorer } from '../../../store/app/slice';
import { setSelectedCompanies } from '../../../store/company/slice';
import { Company } from '../../../store/company/types';
import TickerInput from '../../TickerInput';
import { Text } from '../../Typography';
import FileExplorer from '../../FileExplorer/FileExplorer';
import DocumentViewer from '../../DocumentViewer/DocumentViewer';
import { trackEvent } from '../../../services/tracking';

type Props = {
  searchText?: string;
  leftSidebar?: React.ReactNode;
  main: React.ReactNode;
  embeddedOnlyTicker?: boolean;
};

const CompanyFilesLayout: React.FC<Props> = ({
  leftSidebar,
  main,
  searchText,
  embeddedOnlyTicker = true,
}) => {
  const dispatch = useAppDispatch();
  const { isFileExplorerOpen, isMobile } = useAppSelector((state) => state.app);
  const { documentId } = useAppSelector((state) => state.document);
  const { selectedCompanies } = useAppSelector((state) => state.company);

  const hasSelection = selectedCompanies.length > 0;

  const handleTickerChange = (company: Company | null) => {
    if (company) {
      dispatch(setSelectedCompanies([company]));
      trackEvent('Ticker Selected', { ticker: company.ticker, location: 'CompanyFilesLayout' });
    } else {
      dispatch(setSelectedCompanies([]));
      trackEvent('Ticker Cleared', { location: 'CompanyFilesLayout' });
    }
  };

  const fileExplorerClassName = `${styles.fileExplorerContainer} ${
    isFileExplorerOpen ? styles.open : ''
  } ${isMobile ? styles.mobile : ''}`;

  return (
    <div className={styles.container}>
      {leftSidebar && <div className={styles.sidebar}>{leftSidebar}</div>}
      <div className={styles.mainAndRight}>
        <div className={styles.leftColumn}>
          {hasSelection ? (
            main
          ) : (
            <div className={styles.initialView}>
              <Text size="large" color="gray50" className={styles.title}>
                {searchText || 'Search for a company to quickly learn about it'}
              </Text>
              <div className={styles.tickerInputWrapper}>
                <TickerInput
                  value={null}
                  onChange={handleTickerChange}
                  embeddedOnly={embeddedOnlyTicker}
                  noOptionsMessage={
                    embeddedOnlyTicker
                      ? "No companies found. This company can't be searched yet."
                      : 'No companies found, we may not have data for it yet'
                  }
                />
              </div>
            </div>
          )}
        </div>
        {hasSelection && (
          <div className={styles.rightColumn}>
            <div className={fileExplorerClassName}>
              <FileExplorer
                onClose={() => dispatch(closeFileExplorer())}
                isMobile={isMobile}
                embeddedOnlyTicker={embeddedOnlyTicker}
              />
            </div>
            <div className={styles.documentContainer}>{documentId && <DocumentViewer />}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyFilesLayout;
